import type { Editor } from '@tiptap/core'

/**
 * Editor lifecycle counters (canvas load optimization, task 0.1).
 * Holds counts and timings only, never editor content, so it is safe to expose outside development.
 * Read it in DevTools with `window.__notchEditorDiagnostics()`.
 */
export type EditorCounters = {
  created: number
  destroyed: number
  active: number
  peak: number
  totalCreateMs: number
  maxCreateMs: number
}

export type EditorDiagnostics = EditorCounters & {
  byKind: Record<string, EditorCounters>
}

const emptyCounters = (): EditorCounters => ({
  created: 0,
  destroyed: 0,
  active: 0,
  peak: 0,
  totalCreateMs: 0,
  maxCreateMs: 0
})

let totals = emptyCounters()
let byKind: Record<string, EditorCounters> = {}

function recordCreate(counters: EditorCounters, createMs: number) {
  counters.created++
  counters.active++
  counters.peak = Math.max(counters.peak, counters.active)
  counters.totalCreateMs += createMs
  counters.maxCreateMs = Math.max(counters.maxCreateMs, createMs)
}

function recordDestroy(counters: EditorCounters) {
  counters.destroyed++
  counters.active--
}

export function trackEditor(editor: Editor, kind: string, createMs: number): void {
  if (!byKind[kind]) byKind[kind] = emptyCounters()
  const kindCounters = byKind[kind]
  const generation = totals
  recordCreate(totals, createMs)
  recordCreate(kindCounters, createMs)

  editor.on('destroy', () => {
    // Editors created before a reset must not skew the new counts
    if (generation !== totals) return
    recordDestroy(totals)
    recordDestroy(kindCounters)
  })
}

export function getEditorDiagnostics(): EditorDiagnostics {
  const kinds: Record<string, EditorCounters> = {}
  Object.keys(byKind).forEach(kind => (kinds[kind] = { ...byKind[kind] }))
  return { ...totals, byKind: kinds }
}

export function resetEditorDiagnostics(): void {
  totals = emptyCounters()
  byKind = {}
}

if (typeof window !== 'undefined') {
  window.__notchEditorDiagnostics = getEditorDiagnostics
}
